import { useMemo, useState } from 'react'
import { getAppLocale } from '@/lib/locale'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Building2,
  CheckCircle2,
  Clock,
  Eye,
  Globe,
  MoreHorizontal,
  Plus,
  RefreshCw,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { ApiError } from '../../../lib/api'
import {
  fetchBusinesses,
  inviteBusiness,
  resendBusinessInvitation,
} from '../../businesses/api'
import { useIacrmPlatformBusinesses } from '../../iacrm/hooks'
import { KpiCard, KpiCardSkeleton, kpiSnapshotBadge } from './KpiCard'
import { DashboardSectionHeader } from './DashboardSectionHeader'
import { InviteBusinessDialog } from './InviteBusinessDialog'
import { PageHeader, PageHeaderToolbar } from '@/components/app/PageHeader'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { BusinessInvitePayload } from '../../../types/businesses'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusBadge(status: string, t: (key: string) => string) {
  const styles: Record<string, { labelKey: string; className: string }> = {
    approved: {
      labelKey: 'dashboard.platform.statusActive',
      className: 'border-emerald-400 bg-emerald-500/10 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300',
    },
    pending: {
      labelKey: 'dashboard.platform.statusPending',
      className: 'border-amber-300 bg-amber-500/10 text-amber-800 dark:border-amber-700 dark:bg-amber-500/20 dark:text-amber-300',
    },
    rejected: {
      labelKey: 'dashboard.platform.statusRejected',
      className: 'border-red-300 bg-red-500/10 text-red-800 dark:border-red-700 dark:bg-red-500/20 dark:text-red-300',
    },
  }
  const s = styles[status] ?? { labelKey: '', className: 'border-border bg-muted/40 text-muted-foreground' }
  return (
    <Badge variant="outline" className={s.className}>
      {s.labelKey ? t(s.labelKey) : status}
    </Badge>
  )
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function PlatformDashboardSkeleton() {
  return (
    <section className="app-section">
      <PageHeader
        title={<Skeleton className="h-6 w-52" />}
        right={<Skeleton className="h-9 w-36 rounded-md" />}
      />
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => <KpiCardSkeleton key={i} />)}
      </div>
      <article className="rounded-lg bg-card p-3 sm:p-4">
        <Skeleton className="h-5 w-32" />
        <div className="mt-4 overflow-hidden rounded-lg bg-background/40">
          <div className="h-11 bg-muted/30" />
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-16 border-t border-border/50 bg-card/70 first:border-t-0" />
          ))}
        </div>
      </article>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Platform Dashboard
// ---------------------------------------------------------------------------

export function PlatformDashboard() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [inviteOpen, setInviteOpen] = useState(false)

  const businessesQuery = useQuery({
    queryKey: ['businesses', 'list'],
    queryFn: fetchBusinesses,
  })

  const iacrmQuery = useIacrmPlatformBusinesses()

  const inviteMutation = useMutation({
    mutationFn: (payload: BusinessInvitePayload) => inviteBusiness(payload),
    onSuccess: async (response) => {
      setInviteOpen(false)
      if (response.meta?.mail_delivery_failed) {
        toast.warning(
          response.meta.mail_delivery_error
            ? t('businesses.invite.mailDeliveryFailedWithError', { error: response.meta.mail_delivery_error })
            : t('businesses.invite.mailDeliveryFailed'),
          { id: 'platform-business-invite-toast' },
        )
      } else {
        toast.success(t('businesses.invite.success'), { id: 'platform-business-invite-toast' })
      }
      await queryClient.invalidateQueries({ queryKey: ['businesses'] })
    },
    onError: (error) => {
      toast.error((error as ApiError).message, { id: 'platform-business-invite-toast' })
    },
  })

  const resendMutation = useMutation({
    mutationFn: resendBusinessInvitation,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['businesses'] })
    },
  })

  const businesses = businessesQuery.data?.data ?? []
  const iacrmBusinesses = iacrmQuery.data?.data ?? []

  const summary = useMemo(
    () =>
      businesses.reduce(
        (acc, b) => {
          acc.total++
          if (b.status === 'pending') acc.pending++
          if (b.status === 'approved') acc.approved++
          acc.programs += b.program_count ?? 0
          acc.agents += b.agent_count ?? 0
          acc.transactions += b.transaction_count ?? 0
          return acc
        },
        { total: 0, pending: 0, approved: 0, programs: 0, agents: 0, transactions: 0 },
      ),
    [businesses],
  )

  const topBusinesses = useMemo(() => {
    return [...businesses]
      .sort((a, b) => (b.transaction_count ?? 0) - (a.transaction_count ?? 0))
      .slice(0, 5)
  }, [businesses])

  // Existing IACRM IDs set
  const existingIacrmIds = useMemo(() => {
    const ids = new Set<string>()
    for (const b of businesses) {
      if (b.iacrm_business_id) ids.add(b.iacrm_business_id)
      if (b.slug) ids.add(b.slug)
    }
    return ids
  }, [businesses])

  const isMutating = inviteMutation.isPending || resendMutation.isPending

  if (businessesQuery.isPending) return <PlatformDashboardSkeleton />

  if (businessesQuery.isError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/50 dark:text-red-300">
        {(businessesQuery.error as ApiError).message}
      </div>
    )
  }

  return (
    <section className="app-section">
      <PageHeader
        title={t('dashboard.platform.title')}
        right={
          <PageHeaderToolbar>
            <Button
              size="sm"
              className="gap-2"
              onClick={() => setInviteOpen(true)}
            >
              <Plus className="size-4" aria-hidden />
              {t('dashboard.platform.inviteBusiness')}
            </Button>
          </PageHeaderToolbar>
        }
      />

      {/* ── KPI strip ───────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          title={t('dashboard.platform.kpis.totalBusinesses')}
          value={summary.total.toString()}
          description={t('dashboard.platform.kpis.totalBusinessesDesc')}
          badge={kpiSnapshotBadge(t('dashboard.platform.badge.total'))}
          icon={Building2}
          tone="primary"
        />
        <KpiCard
          title={t('dashboard.platform.kpis.pending')}
          value={summary.pending.toString()}
          description={t('dashboard.platform.kpis.pendingDesc')}
          badge={{
            tone: summary.pending > 0 ? 'warning' : 'neutral',
            label: summary.pending > 0 ? t('dashboard.platform.kpis.pendingBadgeAction') : t('dashboard.platform.kpis.pendingBadgeEmpty'),
            icon: null,
          }}
          icon={Clock}
          tone="warning"
        />
        <KpiCard
          title={t('dashboard.platform.kpis.active')}
          value={summary.approved.toString()}
          description={t('dashboard.platform.kpis.activeDesc')}
          badge={kpiSnapshotBadge(t('dashboard.platform.badge.active'))}
          icon={CheckCircle2}
          tone="success"
        />
        <KpiCard
          title={t('dashboard.platform.kpis.iacrmBusinesses')}
          value={iacrmQuery.isPending ? '...' : iacrmBusinesses.length.toString()}
          description={t('dashboard.platform.kpis.iacrmBusinessesDesc')}
          badge={kpiSnapshotBadge(t('dashboard.platform.badge.source'))}
          icon={Globe}
          tone="info"
        />
      </div>

      {/* ── Top 5 Businesses by Transactions ───────────────────── */}
      <article className="rounded-lg bg-card p-3 sm:p-4">
        <DashboardSectionHeader title={t('dashboard.platform.topBusinesses', 'Top 5 businesses by transactions')} />

        <div className="mt-4 overflow-hidden rounded-lg border border-border">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10 text-center">{t('dashboard.platform.table.number')}</TableHead>
                  <TableHead>{t('dashboard.platform.table.business')}</TableHead>
                  <TableHead className="hidden md:table-cell">{t('dashboard.platform.table.status')}</TableHead>
                  <TableHead className="text-right">{t('dashboard.platform.table.transactions')}</TableHead>
                  <TableHead className="w-10 pe-2 text-end">
                    <span className="sr-only">{t('dashboard.platform.table.actions')}</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topBusinesses.map((item, index) => (
                  <TableRow key={item.id}>
                    <TableCell className="text-center text-xs tabular-nums text-muted-foreground">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/businesses/${item.id}`}
                        className="group -m-1 flex min-w-0 items-center gap-2.5 rounded-md p-1 outline-none transition-colors hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      >
                        <Avatar className="size-9 shrink-0 rounded-md">
                          <AvatarImage src={item.logo_url ?? undefined} alt={item.display_name} className="object-contain" />
                          <AvatarFallback className="rounded-md text-xs font-semibold">
                            {item.display_name.slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="truncate font-medium text-foreground underline-offset-4 group-hover:underline">
                            {item.display_name}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">
                            {item.business_code ?? t('dashboard.codeUnavailable')}
                          </p>
                          <p className="truncate text-[11px] text-muted-foreground/80">{item.legal_name}</p>
                        </div>
                      </Link>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {statusBadge(item.status, t)}
                    </TableCell>
                    <TableCell className="text-right text-sm font-medium">
                      {(item.transaction_count ?? 0).toLocaleString(getAppLocale())}
                    </TableCell>
                    <TableCell className="pe-2 text-end">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button type="button" variant="ghost" size="icon-sm" aria-label={t('common.actions')}>
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem asChild>
                            <Link to={`/businesses/${item.id}`}>
                              <Eye className="mr-2 size-3.5" />
                              {t('dashboard.platform.table.viewDetails')}
                            </Link>
                          </DropdownMenuItem>
                          {item.status === 'pending' ? (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                disabled={isMutating}
                                onClick={() => resendMutation.mutate(item.id)}
                              >
                                <RefreshCw className="mr-2 size-3.5" />
                                {t('dashboard.platform.table.resendInvitation')}
                              </DropdownMenuItem>
                            </>
                          ) : null}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </article>

      {/* ── Invite Dialog ──────────────────────────────────────── */}
      <InviteBusinessDialog
        open={inviteOpen}
        isPending={inviteMutation.isPending}
        error={inviteMutation.isError ? (inviteMutation.error as ApiError) : null}
        existingIacrmIds={existingIacrmIds}
        onClose={() => {
          setInviteOpen(false)
          inviteMutation.reset()
        }}
        onSubmit={(payload) => inviteMutation.mutate(payload)}
      />
    </section>
  )
}
